<script setup lang="ts">
import type { CampaignProductGroup } from '~/composables/useCampaignProductGroups'
import type { HybridProductGroup } from '~/utils/mapHybridProductGroups'
import type { CampaignProduct } from '~/types'

const props = defineProps<{
  productGroup: (CampaignProductGroup | HybridProductGroup) & { isSponsored?: boolean }
  listIndex?: number
}>()

const emit = defineEmits<{
  'impression': [productHandle: string]
  'product-click': [productHandle: string]
  'add-to-cart': [productHandle: string]
}>()

const expanded = ref(false)
const gtm = useGtm()
const rootEl = ref<HTMLElement | null>(null)
const isInView = ref(false)
// Shared by /campaigns and hybrid SRP — resolve from the route, not a hardcoded section.
const pageSection = usePageSection()

const isSponsored = computed(() => !!props.productGroup.isSponsored)

// ProductItem is typed for campaign suppliers; hybrid MappedSupplierEntry is
// runtime-compatible but not assignable (e.g. missing externalId on items).
const primarySupplier = computed(() => props.productGroup.suppliers.at(0) as CampaignProduct | undefined)
const otherSuppliers = computed(() => props.productGroup.suppliers.slice(1) as CampaignProduct[])

provide('martailerSponsoredInView', isInView)
provide('martailerSponsoredTrack', computed(() => {
  if (!isSponsored.value) return null
  return {
    onImpression: (productHandle: string) => emit('impression', productHandle),
    onClick: (productHandle: string) => emit('product-click', productHandle),
    onAddToCart: (productHandle: string) => emit('add-to-cart', productHandle),
  }
}))

useIntersectionObserver(rootEl, ([entry]) => {
  isInView.value = !!isSponsored.value && !!entry?.isIntersecting
}, { threshold: 0.5 })

function handleToggleSupplier() {
  if (!expanded.value) {
    gtm?.trackEvent({
      event: 'view_supplier_options',
      supplier_name: (props.productGroup.suppliers[0] as any).supplier?.name ?? props.productGroup.suppliers[0].name,
      page_section: pageSection,
      item_id: props.productGroup.suppliers[0]?.items[0]?.handle ?? '',
      item_name: props.productGroup.name,
    })
  }
  expanded.value = !expanded.value
}
</script>

<template>
  <div ref="rootEl">
    <SearchCard v-if="productGroup?.suppliers?.length > 0">
      <ProductItem
        :product="primarySupplier!"
        :list-index="listIndex"
        :is-sponsored="isSponsored"
      />
      <template v-if="expanded">
        <template v-for="supplier of otherSuppliers" :key="supplier.name">
          <v-divider />
          <ProductItem :product="supplier" :list-index="listIndex" />
        </template>
      </template>
      <SuppliersList
        v-if="productGroup.suppliers.length > 1"
        :supplier-shown="expanded"
        :suppliers="productGroup.suppliers.slice(1)"
        @toggle-supplier="handleToggleSupplier"
      />
    </SearchCard>
  </div>
</template>
